import { useEffect, useRef, useState } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import { LuCalendarDays, LuChevronDown, LuChevronUp } from "react-icons/lu"
import ReactMarkdown, { defaultUrlTransform } from "react-markdown"
import remarkGfm from "remark-gfm"
import { agendaMediaUrl } from "util/agendaMedia"
import BotAvatar from "./BotAvatar"
import ChatError from "./ChatError"
import InlineSessionCard from "./InlineSessionCard"
import { findAbstractLink } from "components/agenda/AbstractModal"
import { programGuidePath } from "lib/sessionUrl"
import SaveAllButton from "./SaveAllButton"
import ToolActivityIndicator from "./ToolActivityIndicator"
import { STATUS_TOOL_TYPE, statusNote } from "./toolActivityMessages"
import { BOT_BUBBLE_BG, NAVY, PAGE_BG, TITLE_COLOR } from "./constants"

// The native app's own deep links (ebcrf://map/?title=Maps), which the agent
// writes in answers to the app. react-markdown blanks any URL scheme it doesn't
// know to href="", which the webview resolves to this page.
const isAppLink = url => /^ebcrf:/i.test(url ?? "")

export const markdownUrlTransform = url => (isAppLink(url) ? url : defaultUrlTransform(url))

export const markdownComponents = {
    // an app deep link opens in place, like the session cards' links, for the
    // app to catch; anything else opens in a new window
    a: ({ node, ...props }) =>
        isAppLink(props.href) ? <a {...props} /> : <a {...props} target="_blank" rel="noopener noreferrer" />,
    // keep any agenda.crf.org image an agent reply embeds same-origin
    img: ({ node, src, ...props }) => <img {...props} src={agendaMediaUrl(src)} />,
}

// Session cards render only from this tool's output; other tools (e.g.
// FindSessionsByTopicTool) are intermediate steps whose results the agent
// summarizes in text.
const SESSION_CARDS_TOOL_TYPE = "tool-RecommendSessionsTool"

// The agent ends a turn with up to three suggested follow-ups, surfaced as this
// UI-only tool's output. Rendered as clickable pills that send the suggestion as
// the user's next message. See docs/ai-suggested-actions.md for the contract.
const SUGGEST_ACTIONS_TOOL_TYPE = "tool-SuggestNextActionsTool"

function parseToolOutput(output) {
    if (!output) return null
    if (typeof output === "string") {
        try {
            return JSON.parse(output)
        } catch {
            return null
        }
    }
    return output
}

function toolOutputToSessions(output, eventsById) {
    const parsed = parseToolOutput(output)
    if (!parsed?.sessions) return []
    return parsed.sessions.map(s => {
        const id = s.event_id ?? s.id
        const event = eventsById?.[String(id)]
        return {
            id,
            title: s.title,
            // Prefer the authoritative Emma event for the time: its datetime_start is
            // an absolute, offset-aware timestamp. The agent-supplied s.meta/s.start_time
            // is a naive wall-clock, which formatSessionMeta double-shifts when re-parsed
            // in the viewer's timezone (e.g. 9:00 AM PDT shown as 6:00 AM on an ET browser).
            meta: event ? metaFromEvent(event) : s.meta || formatSessionMeta(s),
            // Split time/room for the card's header and location line; without the Emma
            // event the card falls back to the combined meta string.
            ...(event ? timeAndRoomFromEvent(event) : {}),
            // Parent context only for presentations (depth >= 2, the same rule as the card's
            // kind label). Sessions would otherwise get their own breadcrumb, which is just
            // their title again.
            meta2: event ? parentTitleForPresentation(event, eventsById) : s.parent_session || null,
            saved: false,
            calAdded: false,
        }
    })
}

// "Day h:mm a" and room from an authoritative Emma event, converting the absolute
// datetime_start once into the event's own venue timezone.
function timeAndRoomFromEvent(event) {
    const tz = event.venue?.timezone || "America/New_York"
    let time = null
    if (event.datetime_start) {
        const d = new Date(event.datetime_start)
        const day = d.toLocaleDateString("en-US", { weekday: "short", timeZone: tz })
        const clock = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz })
        time = `${day} ${clock}`
    }
    return { time, location: event.venue?.name || null }
}

// Format "Day h:mm a · Room" from an authoritative Emma event.
function metaFromEvent(event) {
    const { time, location } = timeAndRoomFromEvent(event)
    return [time, location].filter(Boolean).join("  ·  ")
}

// Render from the output part — it carries the final, de-duplicated, capped
// list. Always send `prompt` (not `label`) as the next message: the two are
// identical today but the contract keeps them separate so a label can later be
// a terse stand-in for a fuller prompt.
function suggestionsFromParts(parts) {
    const part = parts?.find(p => p.type === SUGGEST_ACTIONS_TOOL_TYPE && p.state === "output-available")
    const parsed = parseToolOutput(part?.output)
    if (!parsed?.suggestions) return []
    return parsed.suggestions.filter(s => s?.label && s?.prompt)
}

function formatSessionMeta(s) {
    const parts = []
    if (s.date) {
        const time = s.time_start ? `${s.date} · ${s.time_start}` : s.date
        parts.push(time)
    } else if (s.start_time) {
        const d = new Date(s.start_time)
        const day = d.toLocaleDateString("en-US", { weekday: "short", timeZone: s.timezone || "America/New_York" })
        const time = d.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            timeZone: s.timezone || "America/New_York",
        })
        parts.push(`${day} ${time}`)
    }
    const venue = s.location || s.venue
    if (venue) {
        // venue is pipe-delimited (building | level | room | area); show the room/area
        const room = venue.split("|").pop().trim()
        parts.push(room || venue)
    }
    if (s.speakers?.length) parts.push(s.speakers.join(", "))
    return parts.join("  ·  ")
}

function parentTitleForPresentation(event, eventsById) {
    if (event.depth == null || event.depth < 2) return null
    return eventsById?.[event.parentId]?.title || null
}

// Only top-level sessions (depth 0 or 1) get an Add to Calendar action.
function calendarEventForSession(id, eventsById) {
    const event = eventsById?.[String(id)]
    if (!event || event.depth > 1) return null
    if (!event.datetime_start || !event.datetime_end) return null
    return {
        id: event.id,
        title: event.title,
        start: new Date(event.datetime_start),
        end: new Date(event.datetime_end),
        location: event.venue?.breadcrumb || event.venue?.name || "",
    }
}

// Session types whose slides are never offered to attendees, even when the Emma
// feed carries a deck link for them. Prefix match on the type name so per-conference
// variants ("Academic Satellite Programs", "… & Networking") are covered. The agent
// applies the same rule server-side (SlideLinks in crf-agenda).
const WITHHELD_SLIDES_SESSION_TYPE_PREFIXES = ["academic satellite program"]

export function slidesWithheldForEvent(event) {
    const type = event?.session_type
    const name = (typeof type === "string" ? type : type?.name) || ""
    const lower = name.toLowerCase()
    return WITHHELD_SLIDES_SESSION_TYPE_PREFIXES.some(prefix => lower.startsWith(prefix))
}

// URL of the session's slide deck PDF, if the Emma event carries one in its links
// and its session type allows slides to be shown.
function slidesUrlForSession(id, eventsById) {
    const event = eventsById?.[String(id)]
    if (slidesWithheldForEvent(event)) return null
    const links = event?.links
    if (!Array.isArray(links)) return null
    const slide = links.find(link => link?.type === "slide" && link?.extension === "pdf" && link?.url)
    return slide ? slide.url : null
}

// URL of the presentation's abstract, if the Emma event carries an `abstract` link.
function abstractUrlForSession(id, eventsById) {
    return findAbstractLink(eventsById?.[String(id)]?.links)?.url || null
}

// The message sent when a card's "Ask about these slides" button is tapped. It reads as
// something the user could have typed, plus an invisible <<event:ID>> marker naming the
// exact presentation, so the agent can read the deck (GetSlideDeckSummaryTool) without
// first searching the agenda for the title. The marker is stripped from the user's
// bubble by userMessageText() but travels to the agent in the message and the history.
export function askAboutSlidesPrompt(session) {
    const where = session.meta ? ` (${session.meta})` : ""
    const marker = session.id ? ` <<event:${session.id}>>` : ""
    return `Tell me about the slides for "${session.title}"${where}${marker}`
}

// Visible text of a user message: its text parts, minus any <<event:ID>> markers a card
// button appended for the agent's benefit.
export function userMessageText(msg) {
    const text =
        msg.parts
            ?.filter(p => p.type === "text")
            .map(p => p.text)
            .join("") || ""
    return text.replace(/\s*<<event:\d+>>/g, "")
}

// Program guide path for a card. The guide lists top-level sessions, found by walking up the
// agenda. A presentation (depth 2+, labelled "Presentation" by sessionKindLabel) opens under its
// session; a session card — including a depth-1 same-titled wrapper — opens the session itself.
function programGuideUrlForSession(id, eventsById) {
    const card = eventsById?.[String(id)]
    if (!card) return null
    let root = card
    while (root.parentId && eventsById[root.parentId]) root = eventsById[root.parentId]
    return card.depth >= 2 ? programGuidePath(root.id, card.id) : programGuidePath(root.id)
}

function sessionKindLabel(id, eventsById) {
    const event = eventsById?.[String(id)]
    if (!event || event.depth == null) return null
    return event.depth >= 2 ? "Presentation" : "Session"
}

// Every presentation under a session (children, grandchildren, ...) flattened into one
// list sorted by start time, for the card's "Show presentations" list. Presentations
// themselves (depth >= 2) get no list. url is the same link the child's "View event"
// would use; it's undefined where no app link is provided.
function presentationsForSession(id, eventsById, sessionAppLink) {
    const event = eventsById?.[String(id)]
    if (!event || event.depth == null || event.depth >= 2) return []
    const all = []
    const walk = e => {
        for (const c of e.children ?? []) {
            all.push(c)
            walk(c)
        }
    }
    walk(event)
    return all
        .sort((a, b) => new Date(a.datetime_start) - new Date(b.datetime_start))
        .map(c => ({
            id: c.id,
            title: c.title,
            // Same venue-timezone clock as the card's own time (eventToSession).
            time: c.datetime_start
                ? new Date(c.datetime_start).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      timeZone: c.venue?.timezone ?? event.venue?.timezone ?? "America/New_York",
                  })
                : null,
            url: sessionAppLink ? sessionAppLink(c.id) : undefined,
        }))
}

// Session cards are hidden behind a toggle so a long recommendation list doesn't
// dominate the chat; the agent's text summary stays visible above the button.
function SessionCardsSection({
    sessions,
    sessionActions,
    eventsById,
    sessionAppLink,
    onAddToCalendar,
    onViewSlides,
    onSend,
}) {
    // "Ask about these slides" rides on the same gate as View Slides (mobile app webview).
    const onAskAboutSlides = onViewSlides && onSend ? session => onSend(askAboutSlidesPrompt(session)) : undefined

    // Short lists aren't worth hiding; show them directly without the toggle.
    const collapsible = sessions.length >= 3
    const [shown, setShown] = useState(false)
    const sessionIds = sessions.map(s => s.id)
    const visible = !collapsible || shown

    return (
        <Box>
            {collapsible && (
                // Pill styled to match the session card actions (InlineSessionCard).
                <Flex
                    as="button"
                    type="button"
                    align="center"
                    gap="6px"
                    w="fit-content"
                    px="14px"
                    py="7px"
                    mt="8px"
                    borderRadius="full"
                    cursor="pointer"
                    bg={NAVY}
                    color="#fff"
                    fontSize="12px"
                    fontWeight="500"
                    lineHeight={1}
                    transition="background 0.15s ease"
                    _hover={{ bg: "#152E58" }}
                    aria-expanded={shown}
                    onClick={() => setShown(s => !s)}
                >
                    <LuCalendarDays size={13} />
                    {shown ? "Hide Sessions" : `Show Sessions (${sessions.length})`}
                    {shown ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
                </Flex>
            )}

            {visible &&
                sessions.map((baseSession, idx) => {
                    const managed = sessionActions?.sessionsById?.[baseSession.id]
                    const session = managed
                        ? { ...baseSession, saved: managed.saved, calAdded: managed.calAdded }
                        : baseSession
                    const kindLabel = sessionKindLabel(session.id, eventsById)
                    const calEvent = calendarEventForSession(session.id, eventsById)
                    // Slides are only surfaced where a viewer is provided (mobile app webview).
                    const slidesUrl = onViewSlides ? slidesUrlForSession(session.id, eventsById) : null
                    return (
                        <InlineSessionCard
                            key={`card-${session.id}-${idx}`}
                            session={session}
                            onToggleSave={sessionActions?.onToggleSave}
                            isAuthenticated={sessionActions?.isAuthenticated}
                            kindLabel={kindLabel}
                            appLinkUrl={sessionAppLink ? sessionAppLink(session.id) : undefined}
                            calEvent={calEvent && { ...calEvent, meta: session.meta, kindLabel }}
                            onAddToCalendar={onAddToCalendar}
                            slidesUrl={slidesUrl}
                            onViewSlides={onViewSlides}
                            onAskAboutSlides={onAskAboutSlides}
                            abstractUrl={abstractUrlForSession(session.id, eventsById)}
                            presentations={presentationsForSession(session.id, eventsById, sessionAppLink)}
                            // The mobile app links cards into the app; the website links its program guide.
                            programGuideUrl={
                                sessionAppLink ? undefined : programGuideUrlForSession(session.id, eventsById)
                            }
                        />
                    )
                })}

            {visible && sessionActions?.isAuthenticated && sessionActions?.onSaveAll && sessionIds.length > 0 && (
                <SaveAllButton sessionIds={sessionIds} sessionActions={sessionActions} />
            )}
        </Box>
    )
}

function renderMessageParts(parts, sessionActions, eventsById, sessionAppLink, onAddToCalendar, onViewSlides, onSend) {
    const elements = []

    // A status line shown while the agent worked stays as a muted, past-tense note above the
    // answer ("I didn't find X in the agenda, so I checked the conference website."), so it's
    // clear why the answer came from where it did.
    parts
        .filter(p => p.type === STATUS_TOOL_TYPE)
        .map(statusNote)
        .filter(Boolean)
        .forEach((message, i) =>
            elements.push(
                <Text key={`status-${i}`} fontSize="12px" fontStyle="italic" color="#64748B" mt={0} mb="6px">
                    {message}
                </Text>,
            ),
        )

    // Render text before tool/card parts regardless of the order the agent emitted them,
    // since the tool typically runs before the model writes its summary.
    const orderedParts = [...parts.filter(p => p.type === "text"), ...parts.filter(p => p.type !== "text")]

    for (let i = 0; i < orderedParts.length; i++) {
        const part = orderedParts[i]

        if (part.type === "text" && part.text?.trim()) {
            elements.push(
                <ReactMarkdown
                    key={`text-${i}`}
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                    urlTransform={markdownUrlTransform}
                >
                    {part.text.trim()}
                </ReactMarkdown>,
            )
        }

        if (part.type === SESSION_CARDS_TOOL_TYPE && part.state === "output-available") {
            const sessions = toolOutputToSessions(part.output, eventsById)
            if (sessions.length === 0) continue
            elements.push(
                <SessionCardsSection
                    key={`sessions-${i}`}
                    sessions={sessions}
                    sessionActions={sessionActions}
                    eventsById={eventsById}
                    sessionAppLink={sessionAppLink}
                    onAddToCalendar={onAddToCalendar}
                    onViewSlides={onViewSlides}
                    onSend={onSend}
                />,
            )
        }
    }

    return elements
}

export const bubbleCss = {
    "& p": { margin: 0 },
    "& p + p": { marginTop: "8px" },
    "& ul, & ol": { margin: "4px 0", paddingLeft: "20px" },
    "& li": { marginBottom: "2px" },
    "& strong": { fontWeight: 600 },
    // Markdown links only — card action buttons can render as anchors (View Event)
    // and keep their button styling, and card presentation links keep the title color.
    "& a:not(.sessionCard__action):not(.sessionCard__presentationLink)": {
        color: "#2563EB",
        textDecoration: "underline",
    },
    "& code": {
        bg: "rgba(0,0,0,0.06)",
        px: "4px",
        py: "1px",
        borderRadius: "4px",
        fontSize: "13px",
    },
    "& pre": {
        bg: "rgba(0,0,0,0.06)",
        p: "8px",
        borderRadius: "6px",
        overflowX: "auto",
        my: "4px",
    },
    // Pin size, leading and color: the theme's global heading styles (globalsCss.js) would
    // otherwise set site-sized, site-colored headings inside the bubble.
    "& h1, & h2, & h3, & h4": {
        fontSize: "15px",
        lineHeight: "20px",
        fontWeight: 500,
        color: "inherit",
        mt: "8px",
        mb: "4px",
    },
    "& h2": { fontSize: "18px", lineHeight: "22px" },
    "& table": {
        width: "100%",
        borderCollapse: "collapse",
        my: "8px",
        fontSize: "13px",
    },
    "& th, & td": {
        border: "1px solid #E2E8F0",
        px: "8px",
        py: "4px",
        textAlign: "left",
    },
    "& th": {
        bg: "rgba(0,0,0,0.04)",
        fontWeight: 600,
    },
}

const START_MESSAGE_DEFAULT = "Hello! Ask me anything about the conference."

// Clickable prompt pills, aligned under the bot bubble. Shared by the opening
// suggested questions and the agent's per-turn suggested next actions. Each item
// is { key, label, value }; clicking sends `value` as the next user message.
function SuggestionPills({ items, onSelect }) {
    return (
        <Flex gap="8px" flexWrap="wrap" pl="42px">
            {items.map(item => (
                <Text
                    key={item.key}
                    fontSize="13px"
                    color="#2563EB"
                    cursor="pointer"
                    px="12px"
                    py="6px"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="#CBD5E1"
                    bg="#fff"
                    mt={0}
                    _hover={{ bg: "#EEF4FF", borderColor: "#2563EB" }}
                    onClick={() => onSelect(item.value)}
                >
                    {item.label}
                </Text>
            ))}
        </Flex>
    )
}

export default function ChatMessagesWithTools({
    messages,
    isLoading,
    error,
    onReset,
    onSend,
    startingMessage,
    suggestedQuestions,
    sessionActions,
    eventsById,
    // When set (id => url), each card shows a "View Event" deep link into the
    // native app's event screen — used by the mobile-app webview.
    sessionAppLink,
    // When set, cards show an "Add to Calendar" button that opens the in-chat
    // calendar drawer with the session's details.
    onAddToCalendar,
    // When set, cards whose Emma event has a slide PDF link show a "View Slides"
    // button that opens the in-chat PDF viewer — used by the mobile-app webview.
    onViewSlides,
}) {
    const bottomRef = useRef(null)

    const loggedToolPartsRef = useRef(new Set())

    useEffect(() => {
        for (const msg of messages) {
            msg.parts?.forEach((part, idx) => {
                if (part.type?.startsWith("tool-") && part.state === "output-available") {
                    const key = `${msg.id}:${idx}`
                    if (loggedToolPartsRef.current.has(key)) return
                    loggedToolPartsRef.current.add(key)
                    console.log("[chatbot] tool output", {
                        messageId: msg.id,
                        partIndex: idx,
                        toolCallId: part.toolCallId,
                        type: part.type,
                        output: part.output,
                    })
                }
            })
        }
    }, [messages])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages, isLoading])

    return (
        <Flex direction="column" gap="14px" p="20px" flex={1} overflowY="auto" bg={PAGE_BG}>
            <Flex gap="10px" align="flex-end" w="100%">
                <BotAvatar />
                <Box bg={BOT_BUBBLE_BG} borderRadius="12px 12px 12px 4px" px="14px" py="10px" maxW="80%">
                    <Text fontSize="14px" color={TITLE_COLOR} lineHeight={1.5} mt={0}>
                        {startingMessage || START_MESSAGE_DEFAULT}
                    </Text>
                </Box>
            </Flex>
            {messages.length === 0 && suggestedQuestions?.length > 0 && (
                <SuggestionPills
                    items={suggestedQuestions.map(q => ({ key: q, label: q, value: q }))}
                    onSelect={onSend}
                />
            )}
            {messages.map((msg, i) => {
                const hasVisibleContent = msg.parts?.some(
                    p =>
                        (p.type === "text" && p.text?.trim()) ||
                        (p.type === SESSION_CARDS_TOOL_TYPE &&
                            p.state === "output-available" &&
                            toolOutputToSessions(p.output, eventsById).length > 0),
                )
                if (!hasVisibleContent) return null

                // Only the latest turn's suggestions are actionable; older ones go
                // stale once the conversation moves on, so don't render them.
                const suggestions = i === messages.length - 1 ? suggestionsFromParts(msg.parts) : []

                return msg.role === "assistant" ? (
                    <Flex key={msg.id} direction="column" gap="8px" w="100%">
                        <Flex gap="10px" align="flex-end" w="100%">
                            <BotAvatar />
                            <Box
                                bg={BOT_BUBBLE_BG}
                                borderRadius="12px 12px 12px 4px"
                                px="14px"
                                py="10px"
                                maxW="80%"
                                fontSize="14px"
                                color={TITLE_COLOR}
                                lineHeight={1.5}
                                css={bubbleCss}
                            >
                                {renderMessageParts(
                                    msg.parts || [],
                                    sessionActions,
                                    eventsById,
                                    sessionAppLink,
                                    onAddToCalendar,
                                    onViewSlides,
                                    onSend,
                                )}
                            </Box>
                        </Flex>
                        {suggestions.length > 0 && (
                            <SuggestionPills
                                items={suggestions.map(s => ({ key: s.prompt, label: s.label, value: s.prompt }))}
                                onSelect={onSend}
                            />
                        )}
                    </Flex>
                ) : (
                    <Flex key={msg.id} justify="flex-end" w="100%">
                        <Box bg={NAVY} borderRadius="12px 12px 4px 12px" px="14px" py="10px" maxW="72%">
                            <Text fontSize="14px" color="#fff" lineHeight={1.5} mt={0}>
                                {userMessageText(msg)}
                            </Text>
                        </Box>
                    </Flex>
                )
            })}
            {isLoading &&
                !(
                    messages[messages.length - 1]?.role === "assistant" &&
                    messages[messages.length - 1]?.parts?.some(p => p.type === "text" && p.text?.trim())
                ) && <ToolActivityIndicator messages={messages} />}
            {error && <ChatError error={error} onReset={onReset} />}
            <div ref={bottomRef} />
        </Flex>
    )
}
